import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import PropTypes from 'prop-types';

const PWAInstallContext = createContext(null);

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

export function PWAInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(isStandalone);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    window.addEventListener('appinstalled', handleInstalled);

    return () => {
      window.removeEventListener(
        'beforeinstallprompt',
        handleBeforeInstallPrompt
      );

      window.removeEventListener('appinstalled', handleInstalled);
    };
  }, []);

  const install = useCallback(async () => {
    if (!deferredPrompt) return false;

    const promptEvent = deferredPrompt;
    setDeferredPrompt(null);

    try {
      await promptEvent.prompt();
      const choice = await promptEvent.userChoice;

      return choice.outcome === 'accepted';
    } catch (error) {
      console.error('[YODA PWA] Installation failed:', error);
      return false;
    }
  }, [deferredPrompt]);

  return (
    <PWAInstallContext.Provider
      value={{
        canInstall: Boolean(deferredPrompt),
        installed,
        install,
      }}
    >
      {children}
    </PWAInstallContext.Provider>
  );
}

PWAInstallProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default function usePWAInstall() {
  const context = useContext(PWAInstallContext);

  if (!context) {
    throw new Error('usePWAInstall must be used inside PWAInstallProvider');
  }

  return context;
}
